import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const ALERT_TYPE_LABELS = {
  low_sales: "Vendas abaixo da média",
  low_ticket: "Ticket médio baixo",
  inactive_customers: "Sem pedidos há 3 dias",
  weak_hour: "Horário fraco",
  wa_disconnected: "WhatsApp desconectado",
};

const LANGUAGE_OPTIONS = [
  { value: "pt", label: "Português" },
  { value: "es", label: "Español" },
];

export default function OwnerAlertSettings() {
  const { authAxios } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authAxios
      .get("/owner-alerts/settings")
      .then((r) => setCfg(r.data))
      .catch(() => toast.error("Erro ao carregar configurações"));
  }, [authAxios]);

  const handleToggleType = (type) => {
    setCfg((prev) => {
      const types = prev.enabled_alert_types || [];
      return {
        ...prev,
        enabled_alert_types: types.includes(type)
          ? types.filter((t) => t !== type)
          : [...types, type],
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authAxios.put("/owner-alerts/settings", cfg);
      setCfg(res.data);
      toast.success("Configurações guardadas");
    } catch {
      toast.error("Erro ao guardar configurações");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return <div className="py-8 text-center text-gray-400 text-sm">A carregar...</div>;
  }

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Configurações de Alertas</h3>

        <label className="flex items-center gap-3 cursor-pointer mb-4">
          <div
            onClick={() => setCfg((p) => ({ ...p, owner_alerts_enabled: !p.owner_alerts_enabled }))}
            className={`w-11 h-6 rounded-full transition-colors ${
              cfg.owner_alerts_enabled ? "bg-green-500" : "bg-gray-300"
            } relative`}
          >
            <span
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                cfg.owner_alerts_enabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </div>
          <span className="text-sm text-gray-700">Enviar alertas por WhatsApp</span>
        </label>

        {cfg.owner_alerts_enabled && (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Número WhatsApp do dono
            </label>
            <input
              type="tel"
              value={cfg.owner_whatsapp_number || ""}
              onChange={(e) => setCfg((p) => ({ ...p, owner_whatsapp_number: e.target.value }))}
              placeholder="+351910000000"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        )}
      </div>

      <div>
        <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
          Tipos de alerta ativos
        </h4>
        {Object.entries(ALERT_TYPE_LABELS).map(([type, label]) => (
          <label key={type} className="flex items-center gap-2 mb-2 cursor-pointer">
            <input
              type="checkbox"
              checked={(cfg.enabled_alert_types || []).includes(type)}
              onChange={() => handleToggleType(type)}
              className="rounded"
            />
            <span className="text-sm text-gray-700">{label}</span>
          </label>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Máximo alertas/dia
          </label>
          <input
            type="number"
            min={1}
            max={20}
            value={cfg.max_alerts_per_day || 5}
            onChange={(e) => setCfg((p) => ({ ...p, max_alerts_per_day: parseInt(e.target.value) || 5 }))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Idioma</label>
          <select
            value={cfg.preferred_language || "pt"}
            onChange={(e) => setCfg((p) => ({ ...p, preferred_language: e.target.value }))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none"
          >
            {LANGUAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Silêncio a partir de
          </label>
          <input
            type="time"
            value={cfg.quiet_hours_start || "22:00"}
            onChange={(e) => setCfg((p) => ({ ...p, quiet_hours_start: e.target.value }))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Retomar às
          </label>
          <input
            type="time"
            value={cfg.quiet_hours_end || "08:00"}
            onChange={(e) => setCfg((p) => ({ ...p, quiet_hours_end: e.target.value }))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none"
          />
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Alertas críticos (ex: WhatsApp desconectado) ignoram o período de silêncio.
      </p>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-blue-600 text-white text-sm font-medium py-2.5 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {saving ? "A guardar..." : "Guardar configurações"}
      </button>
    </div>
  );
}
